import {
  createEffect,
  createMemo,
  createSignal,
  For,
  JSX,
  Show,
} from 'solid-js';
import { CustomTooltip } from '@tcweb/connect-ui';
import { Dynamic } from 'solid-js/web';

export enum MenuPosition {
  LEFT = 'left',
  RIGHT = 'right',
}

export interface VerticalTab<T extends string> {
  name: T;
  tooltip?:
    | string
    | {
        text: string; // ToolTip text
        position: 'bottom' | 'left' | 'right' | 'top' | 'auto'; // ToolTip position
        shortCut?: string; // Keyboard shortcut for the ToolTip
      };
  icon?: string;
  /**
   * Optional icon class to replace the default 'icon-font' class.
   * usually 'modus-icons', or any other class that uses <i class="modus-icons">icon_name</i> formate
   * If not provided, 'icon-font' will be used.   */
  iconClass?: string;
  /**
   * Default is true.
   */
  iconVisible?: boolean;
  /**
   * Default is false.
   */
  disabled?: boolean;
  /**
   * If true, the tab will be positioned at the bottom of the panel.
   * Default is false.
   */
  positionBottom?: boolean;
  /**
   * Optional function that returns the tab's content.
   *
   * If omitted, the tab does not open a content panel and does not change
   * the currently selected tab, but clicking it will still invoke `onTabToggle`.
   */
  content?(): JSX.Element;
}

export interface VerticalTabPanelProps<T extends string> {
  tabs: VerticalTab<T>[];
  menuPosition: MenuPosition;
  /**
   * Default is true.
   */
  menuVisible?: boolean;
  selectedTab?: T;
  /**
   * @deprecated Kept for backwards compatibility with mobile mapping dark mode.
   */
  enableDarkMode?: boolean;
  /**
   * Callback fired when a tab is toggled.
   *
   * Tabs without content still trigger this callback,
   * even though no panel will open.
   */
  onTabToggle?(tab: T, isOpen: boolean): void;
}

export const VerticalTabPanel = <T extends string>(
  props: VerticalTabPanelProps<T>
): JSX.Element => {
  const [selectedTab, setSelectedTab] = createSignal<T | undefined>(undefined);
  const [tabs, setTabs] = createSignal<VerticalTab<T>[]>([]);
  const [visitedTabs, setVisitedTabs] = createSignal<Set<T>>(new Set());

  const centerTabs = createMemo((): VerticalTab<T>[] => {
    return tabs().filter((tab) => tab.positionBottom !== true);
  });

  const bottomTabs = createMemo((): VerticalTab<T>[] => {
    return tabs().filter((tab) => tab.positionBottom === true);
  });

  const handleTabChange = (tab: VerticalTab<T>): void => {
    const isOpen = selectedTab() === tab.name;
    const hasContent = !!tab.content && !tab.disabled;

    if (isOpen) {
      setSelectedTab(undefined);
      props.onTabToggle?.(tab.name, false);
    } else {
      if (hasContent) {
        setSelectedTab(() => tab.name);
      }
      props.onTabToggle?.(tab.name, true);
    }
  };

  const showTab = createMemo((): VerticalTab<T> | undefined => {
    const tab = tabs().find((tab) => tab.name === selectedTab());
    if (!tab || !tab.content || tab.disabled) return undefined;
    return tab;
  });

  createEffect(() => {
    setSelectedTab(() => props.selectedTab);
  });

  createEffect(() => {
    setTabs(() => props.tabs);
  });

  createEffect(() => {
    const active = showTab();
    if (active) {
      setVisitedTabs((prev) => {
        if (prev.has(active.name)) return prev;
        const next = new Set(prev);
        next.add(active.name);
        return next;
      });
    }
  });

  return (
    <div
      data-testid="vertical-tab-panel"
      class="flex h-full panel-shadow text-base-content"
    >
      <Show when={props.menuPosition === MenuPosition.RIGHT}>
        <TabContent
          tabs={tabs()}
          activeTabName={showTab()?.name}
          visitedTabs={visitedTabs()}
        />
      </Show>
      <Show when={props.menuVisible !== false}>
        <div
          data-testid="vertical-tab-menu"
          class="flex flex-col h-full w-10 bg-base-100"
        >
          <div
            role="tablist"
            aria-orientation="vertical"
            class="flex flex-col flex-grow justify-center"
          >
            <Tabs
              tabs={centerTabs()}
              selectedTab={selectedTab() ?? ''}
              handleTabChange={handleTabChange}
            />
          </div>
          <div class="mt-auto">
            <Tabs
              tabs={bottomTabs()}
              selectedTab={selectedTab() ?? ''}
              handleTabChange={handleTabChange}
            />
          </div>
        </div>
      </Show>
      <Show when={props.menuPosition === MenuPosition.LEFT}>
        <TabContent
          tabs={tabs()}
          activeTabName={showTab()?.name}
          visitedTabs={visitedTabs()}
        />
      </Show>
    </div>
  );
};

const Tabs = <T extends string>(props: {
  tabs: VerticalTab<T>[];
  selectedTab: T;
  handleTabChange(tab: VerticalTab<T>): void;
}): JSX.Element => {
  /**
   * Helper to get tooltip text from either string or object format
   */
  const getTooltipText = (tooltip: VerticalTab<T>['tooltip']): string => {
    if (!tooltip) return '';
    if (typeof tooltip === 'string') return tooltip;
    return tooltip.text;
  };

  /**
   * Helper to format tooltip text with keyboard shortcut
   */
  const getFormattedTooltipText = (
    tooltip: VerticalTab<T>['tooltip']
  ): string => {
    if (!tooltip) return '';
    if (typeof tooltip === 'string') return tooltip;

    const text = tooltip.text;
    const shortcut = tooltip.shortCut;

    return shortcut ? `${text} (${shortcut})` : text;
  };

  /**
   * Helper to get tooltip position from object format, mapped to CustomTooltip positions
   */
  const getTooltipPosition = (
    tooltip: VerticalTab<T>['tooltip']
  ): 'top-start' | 'bottom' | 'left' | 'right' | 'auto' | undefined => {
    if (!tooltip || typeof tooltip === 'string') return undefined;
    return tooltip.position === 'top' ? 'top-start' : tooltip.position;
  };

  return (
    <For each={props.tabs}>
      {(tab) => {
        const isObjectTooltip = tab.tooltip && typeof tab.tooltip === 'object';

        const tabElement = (
          <div
            role="tab"
            id={`vertical-tab-menu-${tab.name}`}
            aria-controls={`vertical-tab-content-${tab.name}`}
            aria-selected={props.selectedTab === tab.name}
            aria-label={getTooltipText(tab.tooltip)}
            data-testid={`vertical-tab-menu-${tab.name}`}
            class={`px-2 py-2 flex items-center justify-center text-base-content ${
              tab.disabled ? '' : 'cursor-pointer'
            } ${
              tab.disabled
                ? 'opacity-25 text-base-muted'
                : props.selectedTab === tab.name
                  ? 'bg-base-page text-primary'
                  : 'hover:bg-base-200'
            }`}
            onClick={() => {
              if (tab.disabled) return;
              props.handleTabChange(tab);
            }}
          >
            <i
              aria-hidden="true"
              class={`${tab.iconClass ?? 'icon-font'} ${tab.icon ?? ''}`}
            >
              {tab.iconClass ? tab.icon : ''}
            </i>
          </div>
        );

        return (
          tab.iconVisible !== false && (
            <Show when={isObjectTooltip} fallback={tabElement}>
              <CustomTooltip
                text={getFormattedTooltipText(tab.tooltip)}
                position={getTooltipPosition(tab.tooltip)}
              >
                {tabElement}
              </CustomTooltip>
            </Show>
          )
        );
      }}
    </For>
  );
};

const TabContent = <T extends string>(props: {
  tabs: VerticalTab<T>[];
  activeTabName?: T;
  visitedTabs: Set<T>;
}): JSX.Element => {
  return (
    <For each={props.tabs}>
      {(tab) => {
        const isActive = (): boolean => props.activeTabName === tab.name;
        const hasBeenVisited = (): boolean => props.visitedTabs.has(tab.name);
        const hasContent = (): boolean => !!tab.content && !tab.disabled;

        return (
          <Show when={hasContent() && hasBeenVisited()}>
            <div
              role="tabpanel"
              id={`vertical-tab-content-${tab.name}`}
              aria-labelledby={`vertical-tab-menu-${tab.name}`}
              data-testid={`vertical-tab-content-${tab.name}`}
              class="flex-1 h-full relative bg-base-page text-base-content"
              style={{ display: isActive() ? undefined : 'none' }}
            >
              <Dynamic component={tab.content!} />
            </div>
          </Show>
        );
      }}
    </For>
  );
};
